<script setup>
import { computed, ref, watch, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import ConversationCard from './widgets/conversation/ConversationCard.vue';
import ConversationCardExpanded from 'dashboard/components-next/Conversation/ConversationCard/ConversationCardExpanded.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import ConversationContextMenu from './widgets/conversation/contextMenu/Index.vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  source: { type: Object, required: true },
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  showExpanded: { type: Boolean, default: false },
  isLastPinned: { type: Boolean, default: false },
});

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();

const selectConversation = inject('selectConversation');
const deSelectConversation = inject('deSelectConversation');
const assignAgent = inject('assignAgent');
const assignTeam = inject('assignTeam');
const assignLabels = inject('assignLabels');
const removeLabels = inject('removeLabels');
const updateConversationStatus = inject('updateConversationStatus');
const toggleContextMenu = inject('toggleContextMenu');
const markAsUnread = inject('markAsUnread');
const markAsRead = inject('markAsRead');
const assignPriority = inject('assignPriority');
const isConversationSelected = inject('isConversationSelected');
const deleteConversation = inject('deleteConversation');

// --- Context menu state (shared by both layouts) ---
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

// Reset context menu state when the row is recycled to a different conversation.
watch(
  () => props.source.id,
  () => {
    if (showContextMenu.value) {
      toggleContextMenu(false);
    }
    showContextMenu.value = false;
    contextMenu.value = { x: null, y: null };
  }
);

const currentChat = useMapGetter('getSelectedChat');
const inboxesList = useMapGetter('inboxes/getInboxes');
const accountIdFromStore = useMapGetter('getCurrentAccountId');

const accountId = computed(() => {
  if (route.params.accountId) {
    return Number(route.params.accountId);
  }

  return accountIdFromStore.value;
});

// Inbox context only when the current route or list filter explicitly scopes to an inbox.
// Do not fall back to the Vuex store — it can retain a stale inbox after visiting inbox/4.
const activeInbox = computed(() => {
  if (route.params.inbox_id) {
    return Number(route.params.inbox_id);
  }

  if (props.conversationInbox) {
    return Number(props.conversationInbox);
  }

  return null;
});

const chatMetadata = computed(() => props.source.meta || {});
const assignee = computed(() => chatMetadata.value.assignee || {});
const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() =>
  senderId.value ? store.getters['contacts/getContact'](senderId.value) : {}
);

const isActiveChat = computed(() => {
  if (currentChat.value.id === props.source.id) return true;

  const sourceContactInboxId =
    props.source.contact_inbox_id || props.source.meta?.contact_inbox_id;
  const selectedContactInboxId =
    currentChat.value.contact_inbox_id ||
    currentChat.value.meta?.contact_inbox_id;

  return (
    Boolean(sourceContactInboxId) &&
    sourceContactInboxId === selectedContactInboxId
  );
});

const operationalConversationId = computed(() =>
  store.getters.getOperationalConversationId(props.source)
);

const effectiveConversationStatus = computed(() =>
  store.getters.getEffectiveConversationStatus(props.source)
);

const effectiveUnreadCount = computed(() =>
  store.getters.getEffectiveUnreadCount(props.source)
);

const inbox = computed(() => {
  const inboxId = props.source.inbox_id;
  return inboxId ? store.getters['inboxes/getInbox'](inboxId) : {};
});

const showInboxName = computed(
  () => !activeInbox.value && inboxesList.value.length > 1
);
const isInboxView = computed(() => !!activeInbox.value);
const showAssigneeForExpandedCard = computed(
  () => props.showExpanded || props.showAssignee
);

const conversationPath = computed(() =>
  frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: operationalConversationId.value,
      label: props.label,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  )
);

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  if (isActiveChat.value) return;
  router.push({ path });
};

const onExpandedSelect = checked => {
  if (checked) {
    selectConversation(props.source.id, inbox.value.id);
  } else {
    deSelectConversation(props.source.id, inbox.value.id);
  }
};

const openContextMenu = e => {
  e.preventDefault();
  toggleContextMenu(true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  toggleContextMenu(false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  updateConversationStatus(
    operationalConversationId.value,
    status,
    snoozedUntil
  );
};

const onAssignAgent = agent => {
  assignAgent(agent, [operationalConversationId.value]);
  closeContextMenu();
};

const onAssignLabel = label => {
  assignLabels([label.title], [operationalConversationId.value]);
};

const onRemoveLabel = label => {
  removeLabels([label.title], [operationalConversationId.value]);
};

const onAssignTeam = team => {
  assignTeam(team, operationalConversationId.value);
  closeContextMenu();
};

const onMarkAsUnread = () => {
  markAsUnread(operationalConversationId.value);
  closeContextMenu();
};

const onMarkAsRead = () => {
  markAsRead(operationalConversationId.value);
  closeContextMenu();
};

const onAssignPriority = priority => {
  assignPriority(priority, operationalConversationId.value);
  closeContextMenu();
};

const onDeleteConversation = () => {
  deleteConversation(operationalConversationId.value);
  closeContextMenu();
};

const isPinned = computed(() =>
  store.getters.isThreadPinned(props.source.contact_inbox_id)
);

const onTogglePin = async () => {
  closeContextMenu();
  // The row's own conversation, not the thread's active one: a restricted agent may be allowed to
  // read the former while the latter is out of their reach.
  const payload = {
    conversationId: props.source.id,
    contactInboxId: props.source.contact_inbox_id,
  };

  try {
    if (isPinned.value) {
      await store.dispatch('unpinConversation', payload);
      useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.UNPIN_SUCCESS'));
    } else {
      await store.dispatch('pinConversation', payload);
      useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.PIN_SUCCESS'));
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.CARD_CONTEXT_MENU.PIN_ERROR')
    );
  }
};
</script>

<template>
  <!-- Expanded layout: wide screen + expanded setting -->
  <ConversationCardExpanded
    v-if="showExpanded"
    :chat="source"
    :current-contact="currentContact"
    :assignee="assignee"
    :inbox="inbox"
    :selected="isConversationSelected(source.id)"
    :is-active-chat="isActiveChat"
    :show-assignee="showAssigneeForExpandedCard"
    :show-inbox-name="showInboxName"
    :is-inbox-view="isInboxView"
    :show-pin-indicator="isPinned"
    :is-last-pinned="isLastPinned"
    @select-conversation="onExpandedSelect"
    @de-select-conversation="onExpandedSelect"
    @click="onCardClick"
    @contextmenu="openContextMenu"
  />

  <!-- Default (condensed) layout -->
  <ConversationCard
    v-else
    :chat="source"
    :current-contact="currentContact"
    :assignee="assignee"
    :inbox="inbox"
    :selected="isConversationSelected(source.id)"
    :is-active-chat="isActiveChat"
    :show-assignee="showAssignee"
    :show-inbox-name="showInboxName"
    :show-pin-indicator="isPinned"
    :is-last-pinned="isLastPinned"
    @click="onCardClick"
    @contextmenu="openContextMenu"
    @select-conversation="selectConversation"
    @de-select-conversation="deSelectConversation"
  />

  <!-- Shared context menu for both layouts -->
  <ContextMenu
    v-if="showContextMenu"
    :x="contextMenu.x"
    :y="contextMenu.y"
    @close="closeContextMenu"
  >
    <ConversationContextMenu
      :status="effectiveConversationStatus"
      :inbox-id="inbox.id"
      :priority="source.priority"
      :chat-id="operationalConversationId"
      :has-unread-messages="effectiveUnreadCount > 0"
      :conversation-labels="source.labels"
      :conversation-url="conversationPath"
      :is-pinned="isPinned"
      @update-conversation="onUpdateConversation"
      @assign-agent="onAssignAgent"
      @assign-label="onAssignLabel"
      @remove-label="onRemoveLabel"
      @assign-team="onAssignTeam"
      @mark-as-unread="onMarkAsUnread"
      @mark-as-read="onMarkAsRead"
      @assign-priority="onAssignPriority"
      @delete-conversation="onDeleteConversation"
      @toggle-pin="onTogglePin"
      @close="closeContextMenu"
    />
  </ContextMenu>
</template>
